import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { api, ApiError } from '@shared/api';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import { crmCustomerProfileKeys } from '@features/crm-customer-profile';
import { crmBtProgramLogKeys } from './queries';
import { crmBtProgramLogDeleteResponseSchema } from './schemas';

const assignmentRemovalResponseSchema = crmBtProgramLogDeleteResponseSchema.refine(
  (receipt) => receipt.data.monitorUrl === `/api/v1/operations/${receipt.data.operationId}`
);

const assignmentSchema = z
  .object({
    data: z
      .object({
        profileRef: z.uuid(),
        trainingProgramId: z.uuid(),
        displayName: z.string(),
        programLogCount: z.number().int().nonnegative(),
        isMainProgram: z.boolean(),
        purchasedProgramListed: z.boolean(),
        etag: z.string().regex(/^"[A-Za-z0-9_-]+"$/),
      })
      .strict(),
  })
  .strict();

export type CrmProgramAssignmentPreview = z.infer<typeof assignmentSchema>['data'];

export function crmProgramAssignmentPath(profileRef: string, trainingProgramId: string) {
  return `/api/v1/user-profiles/${encodeURIComponent(profileRef)}/bt-program-assignments/${encodeURIComponent(trainingProgramId)}`;
}

const assignmentKeys = {
  all: ['crm-bt-program-assignments'] as const,
  preview: (viewerAccessScope: string, profileRef: string, trainingProgramId: string) =>
    [...assignmentKeys.all, viewerAccessScope, profileRef, trainingProgramId] as const,
};

export function useCrmProgramAssignmentPreview(
  viewerAccessScope: string,
  profileRef: string,
  trainingProgramId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: assignmentKeys.preview(viewerAccessScope, profileRef, trainingProgramId),
    queryFn: async ({ signal }): Promise<CrmProgramAssignmentPreview> => {
      const result = await api.getWithMetadata(
        crmProgramAssignmentPath(profileRef, trainingProgramId),
        {
          schema: assignmentSchema,
          signal,
        }
      );
      const preview = result.data.data;
      if (
        preview.profileRef !== profileRef ||
        preview.trainingProgramId !== trainingProgramId ||
        result.headers.get('ETag') !== preview.etag
      ) {
        throw new ApiError({
          status: 0,
          code: 'VALIDATION_ERROR',
          message: 'Program assignment preview did not match the selected target.',
        });
      }
      return preview;
    },
    enabled: enabled && Boolean(viewerAccessScope && profileRef && trainingProgramId),
    retry: false,
    staleTime: 0,
  });
}

export function useRemoveCrmProgramAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      profileRef,
      trainingProgramId,
      etag,
      idempotencyKey,
    }: {
      profileRef: string;
      trainingProgramId: string;
      etag: string;
      idempotencyKey: string;
      viewerAccessScope: string;
    }) =>
      api.delete(crmProgramAssignmentPath(profileRef, trainingProgramId), {
        headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey },
        schema: assignmentRemovalResponseSchema,
      }),
    onSuccess: (_receipt, variables) => {
      clearQueryResponseCache();
      void queryClient.invalidateQueries({
        queryKey: crmBtProgramLogKeys.all,
        refetchType: 'none',
      });
      void queryClient.cancelQueries({ queryKey: crmBtProgramLogKeys.all });
      queryClient.removeQueries({ queryKey: crmBtProgramLogKeys.all });
      queryClient.removeQueries({ queryKey: assignmentKeys.all });
      void queryClient.invalidateQueries({
        queryKey: crmCustomerProfileKeys.detail(variables.viewerAccessScope, variables.profileRef),
      });
    },
  });
}
